import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface User {
  email: string;
  name: string;
  role: 'ADMIN' | 'WYNDER_TEAM_LEAD' | 'WYNDER_TOUR_CURATOR' | 'WYNDER_SALES' | 'WYNDER_EMPLOYEE' | 'WYNDER_ACCOUNTS';
  agency_id: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  userRole: string | null;
  isAdmin: boolean;
  isTeamLead: boolean;
  canDelete: boolean;
  canConvert: boolean;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user: auth0User, isAuthenticated, getAccessTokenSilently, logout } = useAuth0();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const exchangeToken = async () => {
      if (!isAuthenticated || !auth0User?.email) {
        setLoading(false);
        return;
      }

      try {
        // Exchange Auth0 token for our JWT (proxy forwards to :8000)
        const response = await fetch('/api/auth/okta-callback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_email: auth0User.email })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Authentication failed' }));
          console.error('Auth failed:', response.status, errorData);
          
          // Set error and logout
          setError(errorData.detail || 'You do not have access to this application.');
          sessionStorage.clear();
          logout({ logoutParams: { returnTo: window.location.origin } });
          return;
        }

        const data = await response.json();
        
        // Store JWT and user info
        setToken(data.token);
        setUser(data.user);
        sessionStorage.setItem('jwt_token', data.token);
        sessionStorage.setItem('user', JSON.stringify(data.user));
      } catch (error) {
        console.error('Auth error:', error);
        setError('Authentication error. Please contact administrator.');
        sessionStorage.clear();
        logout({ logoutParams: { returnTo: window.location.origin } });
      } finally {
        setLoading(false);
      }
    };

    // Try to restore from sessionStorage first
    const storedToken = sessionStorage.getItem('jwt_token');
    const storedUser = sessionStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setLoading(false);
    } else if (isAuthenticated) {
      exchangeToken();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, auth0User]);

  const isAdmin = user?.role === 'ADMIN';
  const isTeamLead = user?.role === 'WYNDER_TEAM_LEAD';
  const canDelete = isAdmin; // Only admin
  const canConvert = isAdmin || isTeamLead; // Admin or Team Lead
  const userRole = user?.role || null;

  return (
    <AuthContext.Provider value={{ user, token, userRole, isAdmin, isTeamLead, canDelete, canConvert, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
